import {
  Component,
  inject,
  output,
  signal,
  WritableSignal,
  effect,
  model,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { svgIconKeys } from '../../../constants/svg-icon-keys';
import { TypedTemplateVariableDirective } from '../../../global/directives/typed-template-variable.directive';
import {
  LocalStorageService,
  LocalStorageKeys,
} from '../../../global/services/local-storage.service';
import { AccountsStore } from '../../store/accounts/accounts.store';
import { AccountContract } from '../../store/accounts/types';
import { daysBetweenDates } from '../../utils/days-between-dates';
import { TranslocoModule } from '@jsverse/transloco';
import { AccountSearchItemComponent } from '../account-search-item/account-search-item.component';
import { getToday } from '../../utils/get-today';

@Component({
  selector: 'blindspot-accounts-search',
  styleUrls: ['./accounts-search.component.scss'],
  imports: [
    ProgressSpinnerModule,
    IconFieldModule,
    InputIconModule,
    AutoCompleteModule,
    FormsModule,
    TypedTemplateVariableDirective,
    ButtonModule,
    TranslocoModule,
    AccountSearchItemComponent,
  ],
  template: `
    <div class="container" [class.dark]="variant() === 'dark'">
      <i class="pi pi-search"></i>
      <p-autocomplete
        [(ngModel)]="currentSearch"
        [suggestions]="searchItems()"
        [lazy]="true"
        appendTo="body"
        (onSelect)="onSelect($event.value)"
        [placeholder]="'navigation.searchPlaceholder' | transloco"
        optionLabel="name"
        [showClear]="showClear()"
        (onClear)="clear.emit()"
        fluid
      >
        <ng-template let-account #item [typedTemplateVariable]="searchItems()">
          <blindspot-account-search-item
            [account]="account"
            [currentSearch]="currentSearch()"
            [today]="today"
            variant="dark"
          />
        </ng-template>
        <ng-template pTemplate="empty">
          @if (accountsStore.isLoading('searchAccounts')) {
            <div class="loader">
              <p-progress-spinner
                strokeWidth="4"
                [style]="{ height: '50px', width: '50px' }"
              />
            </div>
          } @else {
            <span> {{ 'navigation.noResults' | transloco }} </span>
          }
        </ng-template>
      </p-autocomplete>
    </div>
  `,
})
export class AccountsSearchComponent {
  public accountsStore = inject(AccountsStore);
  public showClear = input(false);
  public variant = input<'default' | 'dark'>('default');
  public prefillAccount = input<AccountContract | undefined>(undefined);
  public orgId = input<string | undefined>(undefined);
  public clusterId = input<string | undefined>(undefined);
  public limitToSalesRepScope = input.required<boolean>();
  public selectAccount = output<AccountContract>();
  public clear = output<void>();
  public currentSearch = model('');
  public searchItems: WritableSignal<AccountContract[]> = signal([]);
  public today = getToday();

  constructor() {
    effect(() => {
      if (this.currentSearch() && typeof this.currentSearch() === 'string') {
        const orgId = this.orgId();
        const clusterId = this.clusterId();

        if (this.limitToSalesRepScope()) {
          this.accountsStore.searchAccountsForSalesRep({
            searchString: this.currentSearch(),
          });
        } else if (!!orgId && !!clusterId) {
          this.accountsStore.searchAccountsInCluster({
            salesOrganizationId: orgId,
            clusterId: clusterId,
            query: this.currentSearch(),
          });
        }
      }
    });

    effect(() => {
      const searchResults = this.accountsStore.searchResults();
      const searchResultsCopy = [...(searchResults ?? [])];

      searchResultsCopy.sort((a, b) => {
        if (a.nextVisitDate && b.nextVisitDate) {
          return (
            new Date(a.nextVisitDate).getTime() -
            new Date(b.nextVisitDate).getTime()
          );
        } else if (a.nextVisitDate) {
          return -1; // a has next visit date, b does not
        } else if (b.nextVisitDate) {
          return 1; // b has next visit date, a does not
        }
        return (
          new Date(a.idealNextVisitDate).getTime() -
          new Date(b.idealNextVisitDate).getTime()
        );
      });

      this.searchItems.set(searchResultsCopy ?? []);
    });

    effect(() => {
      this.currentSearch.set(this.prefillAccount()?.name ?? '');
    });
  }

  public onSelect(account: AccountContract): void {
    this.currentSearch.set(account.name);
    this.selectAccount.emit(account);
  }

  public stopImpersonation(): void {
    LocalStorageService.remove(LocalStorageKeys.IMPERSONATION_TOKEN);
    window.location.reload();
  }

  protected readonly daysBetweenDates = daysBetweenDates;
  protected readonly svgIconKeys = svgIconKeys;
}
